<template>
  <header class="header-root">
    <div class="header-inner">

      <!-- 로고 영역 -->
      <div class="logo-area">
        <img
          :src="logo"
          alt="logo"
          @click="handleLogo"
          class="logo-img"
        />
      </div>

      <!-- 스페이서: 로고를 왼쪽, 나머지를 오른쪽으로 밀어냄 -->
      <div class="header-spacer"></div>

      <!-- 네비게이션 (오른쪽 배치) -->
      <nav class="nav-container">
        <button
          v-for="nav in navItems"
          :key="nav.key"
          @click="nav.action"
          class="nav-item"
          :class="{ 'nav-item--active': isActiveRoute(nav.route) }"
        >
          <span class="nav-item-inner">
            <i :class="[nav.icon, 'nav-icon']"></i>
            <span class="nav-label">{{ nav.label }}</span>
          </span>
          <!-- 하단 인디케이터 바 -->
          <span class="nav-indicator"></span>
        </button>
      </nav>

      <!-- 구분선 -->
      <div class="header-divider"></div>

      <!-- 우측 영역 -->
      <div class="header-right">
        <div v-if="user" class="user-chip">
          <div class="user-avatar">
            <i class="mdi mdi-account"></i>
          </div>
          <span class="user-name">{{ user.name || user.username || '사용자' }}</span>
        </div>

        <button @click="handleLogout" class="logout-btn">
          <i class="mdi mdi-logout"></i>
          <span>로그아웃</span>
        </button>
      </div>

    </div>
  </header>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'

import logo from '@/assets/logo.svg'

const store = useStore()
const router = useRouter()
const route = useRoute()

const user = computed(() => store.state.auth.user)

const navItems = [
  { key: 'insight', label: 'INSIGHT', icon: 'mdi mdi-lightbulb-on-outline', route: '/pub/insight', action: () => router.push('/pub/insight') },
  { key: 'qa', label: 'Q&A', icon: 'mdi mdi-comment-question-outline', route: '/pub/qa', action: () => router.push('/pub/qa') },
  { key: 'status', label: 'STATUS BOARD', icon: 'mdi mdi-view-dashboard-outline', route: '/pub/StatusBoard', action: () => router.push('/pub/StatusBoard') },
]

function isActiveRoute(path) {
  return route.path.startsWith(path)
}

const handleLogout = async () => {
  try {
    await store.dispatch('auth/logout')
    router.push('/')
  } catch (error) {
    console.error('로그아웃 실패:', error)
    store.commit('handleNotify', { message: '로그아웃 중 오류가 발생했습니다.' })
  }
}

const handleLogo = async () => {
  await router.push({ name: 'MainPub' })
}
</script>

<style scoped>
/* ── Root ── */
.header-root {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 50;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.04),
    0 4px 24px rgba(0, 0, 0, 0.03);
}

.header-inner {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  padding: 0 24px;
  height: 56px;
  gap: 0;
}

/* ── Logo ── */
.logo-area {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.logo-img {
  height: 22px;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.logo-img:hover {
  opacity: 0.75;
  transform: scale(1.03);
}

/* ── Spacer ── */
.header-spacer {
  flex: 1;
}

/* ── Divider ── */
.header-divider {
  width: 1px;
  height: 24px;
  margin: 0 20px;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(0, 0, 0, 0.1) 50%,
    transparent 100%
  );
  flex-shrink: 0;
}

/* ── Navigation ── */
.nav-container {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.nav-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 18px;
  height: 56px;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: background 0.2s ease;
}

.nav-item:hover {
  background: rgba(0, 0, 0, 0.02);
}

.nav-item-inner {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: #9ca3af;
  transition: color 0.25s ease;
  white-space: nowrap;
}

.nav-item:hover .nav-item-inner {
  color: #4b5563;
}

.nav-item--active .nav-item-inner {
  color: #7c3aed;
  font-weight: 600;
}

/* ── Icon ── */
.nav-icon {
  font-size: 18px;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
              color 0.25s ease;
}

.nav-item:hover .nav-icon {
  transform: translateY(-1px);
}

.nav-item--active .nav-icon {
  animation: icon-pop 0.3s ease;
}

@keyframes icon-pop {
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}

.nav-label {
  line-height: 1;
}

/* ── Bottom Indicator Bar ── */
.nav-indicator {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 2.5px;
  border-radius: 2px 2px 0 0;
  background: #7c3aed;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 0 0 transparent;
}

.nav-item:hover .nav-indicator {
  width: 20px;
  background: #d1d5db;
}

.nav-item--active .nav-indicator {
  width: 100%;
  background: #7c3aed;
  box-shadow: 0 -1px 8px rgba(124, 58, 237, 0.25);
}

/* ── Right Section ── */
.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

/* User Chip */
.user-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px 4px 4px;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.04);
}

.user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7c3aed, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 14px;
  box-shadow: 0 2px 6px rgba(124, 58, 237, 0.3);
}

.user-name {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Logout Button */
.logout-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 500;
  color: #9ca3af;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
}

.logout-btn i {
  font-size: 15px;
  transition: transform 0.2s ease;
}

.logout-btn:hover {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.06);
  border-color: rgba(239, 68, 68, 0.1);
}

.logout-btn:hover i {
  transform: translateX(2px);
}
</style>
